import { ref } from 'vue';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  duration?: number;
}

const toasts = ref<Toast[]>([]);

let toastIdCounter = 0;

function generateToastId(): string {
  return `toast-${Date.now()}-${toastIdCounter++}`;
}

export function useToast() {
  const show = (message: string, type: ToastType = 'info', options?: { title?: string; duration?: number }) => {
    const toast: Toast = {
      id: generateToastId(),
      type,
      message,
      title: options?.title,
      duration: options?.duration ?? 5000,
    };
    
    toasts.value.push(toast);
    
    // Auto-dismiss after duration
    if (toast.duration && toast.duration > 0) {
      setTimeout(() => {
        remove(toast.id);
      }, toast.duration);
    }
    
    return toast.id;
  };
  
  const remove = (id: string) => {
    const index = toasts.value.findIndex((t) => t.id === id);
    if (index > -1) {
      toasts.value.splice(index, 1);
    }
  };
  
  const clear = () => {
    toasts.value = [];
  };
  
  const success = (message: string, options?: { title?: string; duration?: number }) => {
    return show(message, 'success', options);
  };
  
  const error = (message: string, options?: { title?: string; duration?: number }) => {
    return show(message, 'error', options);
  };
  
  const warning = (message: string, options?: { title?: string; duration?: number }) => {
    return show(message, 'warning', options);
  };
  
  const info = (message: string, options?: { title?: string; duration?: number }) => {
    return show(message, 'info', options);
  };
  
  return {
    toasts,
    show,
    remove,
    clear,
    success,
    error,
    warning,
    info,
  };
}
